// ─── Wireframe: Wholesale Monthly Forecast ───────────────────────────────────
// Mirrors the team's spreadsheet: units per product × customer × month.
// Viewer is "Đỗ Hồng Phương Thảo" — edits own customers' future months only.
// Horizon ends at the current Top-Down plan's end date (Dec 2026 here).
import { useMemo, useState } from "react";
import { Badge, Button, cn } from "../components/primitives";

const MONTHS = [
  { key: "2026-05", label: "05/26", state: "past" },
  { key: "2026-06", label: "06/26", state: "past" },
  { key: "2026-07", label: "07/26", state: "current" },
  { key: "2026-08", label: "08/26", state: "future" },
  { key: "2026-09", label: "09/26", state: "future" },
  { key: "2026-10", label: "10/26", state: "future" },
  { key: "2026-11", label: "11/26", state: "future" },
  { key: "2026-12", label: "12/26", state: "future" },
];

const YOU = "Đỗ Hồng Phương Thảo";
const THAO = "Nguyễn Thị Ngọc Thảo";
const short = (n) => n.split(" ").slice(-2).join(" ");

const PRODUCTS = [
  {
    id: "9757", sku: "Chacott",
    rows: [
      { id: "r1", customer: "AEON VIETNAM CO., LTD", pic: THAO, v: { "2026-05": 1008, "2026-06": 1008, "2026-07": 1008, "2026-08": 1008, "2026-09": 1008 } },
      { id: "r2", customer: "CÔNG TY CP ANHMY FOUNDATION", pic: YOU, v: { "2026-05": 10320, "2026-06": 10560, "2026-07": 10800, "2026-08": 11040, "2026-09": 11280 } },
      { id: "r3", customer: "CÔNG TY TNHH TM MAI VÀNG", pic: YOU, v: { "2026-05": 2280, "2026-06": 2400, "2026-07": 2520, "2026-08": 2640 } },
      { id: "r4", customer: "HASAKI", pic: THAO, v: { "2026-05": 5040, "2026-07": 5040, "2026-08": 5040 } },
      { id: "r5", customer: "GUARDIAN", pic: YOU, v: { "2026-05": 50, "2026-06": 50, "2026-07": 50, "2026-08": 50 } },
    ],
  },
  {
    id: "13831", sku: "HAS",
    rows: [
      { id: "r6", customer: "AEON VIETNAM CO., LTD", pic: THAO, v: { "2026-05": 50, "2026-06": 50, "2026-07": 50, "2026-08": 50 } },
      { id: "r7", customer: "Lagihitech", pic: YOU, v: { "2026-05": 120, "2026-06": 150, "2026-07": 200, "2026-08": 200 } },
      { id: "r8", customer: "ĐIỆN MÁY XANH", pic: YOU, v: { "2026-05": 15, "2026-06": 15, "2026-07": 15, "2026-08": 15 } },
    ],
  },
  {
    id: "11136", sku: "HF",
    rows: [
      { id: "r9", customer: "CÔNG TY CP DƯỢC PHẨM FPT LONG CHÂU", pic: THAO, v: { "2026-05": 1200, "2026-06": 1000, "2026-07": 1200, "2026-08": 1200 } },
      { id: "r10", customer: "CÔNG TY CP THỜI TRANG ELLE AIMEE VN", pic: YOU, v: { "2026-05": 5, "2026-06": 5, "2026-07": 5 } },
    ],
  },
];

const fmt = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString());

export default function MonthlyForecast() {
  const [values, setValues] = useState(() => {
    const m = {};
    PRODUCTS.forEach((p) => p.rows.forEach((r) => { m[r.id] = { ...r.v }; }));
    return m;
  });
  const [collapsed, setCollapsed] = useState(new Set());

  const setCell = (rid, mo, raw) => {
    const v = raw === "" ? null : Math.max(0, Math.round(Number(raw) || 0));
    setValues((prev) => ({ ...prev, [rid]: { ...prev[rid], [mo]: v } }));
  };
  const toggle = (pid) =>
    setCollapsed((prev) => {
      const n = new Set(prev);
      n.has(pid) ? n.delete(pid) : n.add(pid);
      return n;
    });

  const subtotal = (p, mo) => p.rows.reduce((s, r) => s + (values[r.id]?.[mo] || 0), 0);
  const grand = useMemo(
    () => Object.fromEntries(MONTHS.map((m) => [m.key, PRODUCTS.reduce((s, p) => s + subtotal(p, m.key), 0)])),
    [values]
  );

  return (
    <div className="min-h-screen bg-slate-50 p-8 font-sans">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-1">
          <div>
            <div className="text-xs text-slate-400 mb-1">Wireframe · not real data</div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Wholesale Forecast</h1>
            <p className="text-sm text-slate-500 mt-1">
              Planned <span className="font-medium">units</span> per product × customer × month. Months lock when they
              start. Grid runs to <span className="font-medium">12/26</span> — end of the current Top-Down plan. You are{" "}
              <span className="font-medium text-slate-700">{YOU}</span>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">Import spreadsheet</Button>
            <Button variant="default" size="sm">+ Add product</Button>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left font-medium text-slate-500 px-4 py-3 min-w-72">Product / Customer</th>
                <th className="text-left font-medium text-slate-500 px-2 py-3 min-w-32">PIC</th>
                {MONTHS.map((m) => (
                  <th key={m.key} className={cn("px-3 py-3 text-right font-medium min-w-20", m.state === "future" ? "text-slate-700" : "text-slate-400")}>
                    <div>{m.label}</div>
                    <div className="text-[10px] font-normal uppercase tracking-wide">
                      {m.state !== "future" ? <span>🔒 {m.state === "current" ? "this mo" : "locked"}</span> : <span>&nbsp;</span>}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PRODUCTS.map((p) => {
                const isCollapsed = collapsed.has(p.id);
                return [
                  <tr key={p.id} className="bg-slate-50/80 border-b border-slate-100 cursor-pointer select-none" onClick={() => toggle(p.id)}>
                    <td className="px-4 py-2 font-medium text-slate-700" colSpan={2}>
                      <span className="text-slate-400 mr-1.5">{isCollapsed ? "▸" : "▾"}</span>
                      {p.sku}
                      <span className="text-slate-400 font-normal ml-2 text-xs">#{p.id} · {p.rows.length} customers</span>
                    </td>
                    {MONTHS.map((m) => (
                      <td key={m.key} className={cn("px-3 py-2 text-right font-semibold tabular-nums", m.state === "future" ? "text-slate-700" : "text-slate-400")}>
                        {fmt(subtotal(p, m.key) || null)}
                      </td>
                    ))}
                  </tr>,
                  ...(isCollapsed ? [] : [
                    ...p.rows.map((r) => {
                      const mine = r.pic === YOU;
                      return (
                        <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="px-4 py-1.5 pl-9 text-slate-700 truncate max-w-72">{r.customer}</td>
                          <td className="px-2 py-1.5">
                            {mine
                              ? <Badge variant="info">you</Badge>
                              : <span className="text-xs text-slate-400">{short(r.pic)}</span>}
                          </td>
                          {MONTHS.map((m) => {
                            const v = values[r.id]?.[m.key];
                            if (m.state === "future" && mine) {
                              return (
                                <td key={m.key} className="px-1 py-1 text-right">
                                  <input
                                    type="number" min="0" placeholder="—" value={v ?? ""}
                                    onChange={(e) => setCell(r.id, m.key, e.target.value)}
                                    className="w-[4.5rem] text-right tabular-nums text-sm border border-slate-200 rounded-md px-1.5 py-1 bg-white focus:border-slate-400 focus:outline-none placeholder:text-slate-300"
                                  />
                                </td>
                              );
                            }
                            return (
                              <td key={m.key} className={cn("px-3 py-1.5 text-right tabular-nums", m.state === "future" ? "text-slate-500" : "text-slate-400")}>
                                {fmt(v)}
                                {m.state === "past" && v != null && <div className="text-[10px] text-slate-300">actual: soon</div>}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    }),
                    <tr key={`${p.id}-add`} className="border-b border-slate-100">
                      <td colSpan={2 + MONTHS.length} className="px-4 py-1 pl-9">
                        <button className="text-xs text-blue-600 hover:underline bg-transparent border-none cursor-pointer p-0">+ Add customer to {p.sku}</button>
                      </td>
                    </tr>,
                  ]),
                ];
              })}
              <tr className="border-t-2 border-slate-200 bg-white">
                <td className="px-4 py-2.5 font-semibold text-slate-900" colSpan={2}>Total units</td>
                {MONTHS.map((m) => (
                  <td key={m.key} className={cn("px-3 py-2.5 text-right font-semibold tabular-nums", m.state === "future" ? "text-slate-900" : "text-slate-400")}>
                    {fmt(grand[m.key] || null)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-slate-400">
          <span>🔒 Past &amp; current months are read-only (locked at month start)</span>
          <span>You edit only rows where you are PIC — others’ numbers are visible</span>
          <span>Horizon = current Top-Down plan end date</span>
          <span>“actual: soon” = forecast-vs-actual units, once orders carry customer info</span>
        </div>
      </div>
    </div>
  );
}
